import type { TCoreBillingFormInstance } from 'core/checkout/CoreBillingForm';
import type { TPaymentBillingFormInstance } from 'core/checkout/PaymentBillingForm';
import type { TOrderModel } from 'types/models/order';

import { getUrlParams } from 'widgets/toolbox/util';
import type { TWidget } from 'widgets/Widget';

/**
 * @description Checkout Shipping step logic
 *
 * @param BillingStepMixin Base widget for extending
 */
function CoreBillingStepMixinClassCreator(BillingStepMixin: import('widgets/checkout/BillingStepMixin').TBillingStepMixin) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class BillingStepMixin
     * @augments TCheckoutMgr
     * @subcategory checkout
     * @classdesc Represents shipping step component with the next features:
     * 1. Manage Shipping step state on the checkout
     * 2. Submit shipping form
     */
    class CoreBillingStepMixin extends BillingStepMixin {
        prefs() {
            return {
                paymentFormID: 'paymentBillingForm',
                ...super.prefs()
            };
        }

        updateBillingStateEv(order, customer) {
            this.getById(this.prefs().billingFormID, (billingFormComponent: TCoreBillingFormInstance) =>
                billingFormComponent.updateState(order, customer)
            );
        }

        /**
         * @description Activate billing step
         */
        billingActivate() {
            this.getById(this.prefs().paymentFormID, (billingFormComponent: TPaymentBillingFormInstance) => {
                billingFormComponent.activate({
                    order: this.order,
                    customer: this.customer
                });
                const urlParams = <{ paymentError: string }>getUrlParams();

                if (urlParams.paymentError) {
                    billingFormComponent.setError(urlParams.paymentError.toString());

                    this.eventBus().emit('checkout.billing.step.error', this);
                }
            });
            this.eventBus().emit('checkout.billing.step.activated', this);
        }

        /**
         * @description Submit billing form
         * @param submitButtons Event target
         */
        billingSubmit(...submitButtons) {
            this.submitButtons = submitButtons.flat();
            this.submitButtons.forEach(target => {
                if ('skipPrivacyValidation' in target) {
                    this.skipPrivacyValidation = target.skipPrivacyValidation;
                }
            });

            if (this.prefs().skipSummaryStep && !this.callIfExists('validatePlaceOrderForm', this.skipPrivacyValidation)) {
                return;
            }

            this.getById(this.prefs().paymentFormID, (billingFormComponent: TPaymentBillingFormInstance) => {
                billingFormComponent.submit();
            });
        }

        /**
         * @description Scrolls error messages into viewport
         */
        scrollToError() {
            this.emitWithoutContext('scrolltoerror');
        }

        /**
         * @description Emit error event with sent params
         * @param widget - widget
         * @param error - error
         */
        showError(widget: TWidget, error: Event): void {
            this.emitWithoutContext('error', widget, error);
        }

        /**
         * @description Billing submission result handler
         * @param billingForm - Billing form widget
         * @param data - Submission response from server
         * @listens BillingForm#submit
         */
        billingSubmitted(billingForm: TCoreBillingFormInstance, data: Record<string, unknown>) {
            if (this.currentStep !== 'billing') {
                this.callIfExists('updateSummaryAndNext', billingForm, data);
            }
        }

        /**
         * @description Payment billing submission result handler
         * @param paymentBillingForm - Payment billing form widget
         * @param data - Submission response from server
         * @listens PaymentBillingForm#submit
         */
        paymentSubmitted(paymentBillingForm: TPaymentBillingFormInstance, data: Record<string, unknown>) {
            if (data.order) {
                this.order = <TOrderModel>data.order;
            }

            // Update place order URL for specific payment methods (e.g. Afterpay)
            if (data.urlPlaceOrder) {
                this.setPref('urlPlaceOrder', data.urlPlaceOrder as string);
            }

            if (this.prefs().skipSummaryStep) {
                this.callIfExists('updateSummary', data?.order, true);
                this.callIfExists('summarySubmit', this.submitButtons, this.skipPrivacyValidation);
            } else {
                this.callIfExists('updateSummaryAndNext', paymentBillingForm, data);
            }
        }
    }

    return CoreBillingStepMixin;
}

export default CoreBillingStepMixinClassCreator;
